<template>
  <div class="py-5 md:px-5">
    <PageHeader class="container" title="Customers">
      <router-link :to="{ name: 'CustomerCreate' }" class="btn btn-primary">
        Add Customer
      </router-link>
    </PageHeader>

    <div class="container">
      <!-- List -->
      <div class="card">
        <!-- Header -->
        <div class="flex flex-col justify-between p-4 border-b md:flex-row lg:p-5">
          <div class="grid grid-cols-2 gap-3 md:flex">
            <!-- Search -->
            <fieldset class="relative col-span-2">
              <fa :icon="['far', 'search']" class="absolute top-0 left-0 m-2 text-gray-400 pointer-events-none" />
              <input type="text" class="py-1 pl-8 form-input" placeholder="Search..." v-model="filter.search" @keydown.enter="applyFilter">
            </fieldset>
            <!-- Sort -->
            <sort-dropdown :options="sortOptions" />
          </div>
        </div>

        <!-- Loading -->
        <Loading v-if="loading" class="p-6" />

        <!-- Empty -->
        <div v-else-if="customers.meta.total < 1 && !$route.query.filter" class="max-w-xs py-12 mx-auto text-center">
          <div class="mb-2 text-lg font-bold">
            No customers have been added yet.
          </div>
          <div class="text-gray-500">
            Customers that register through your booking form or customer portal will show up here. You can also create customers manually.
          </div>
          <router-link :to="{ name: 'CustomerCreate' }" class="mt-4 btn btn-secondary">
            Add a Customer
          </router-link>
        </div>

        <!-- No Results -->
        <div v-else-if="customers.meta.total < 1 && $route.query.filter" class="max-w-xs py-12 mx-auto text-center">
          <div class="mb-2 text-lg font-bold">
            No customers matching your filters.
          </div>
          <div class="text-gray-500">
            Try changing your filter settings, or clear the filter to see all customers.
          </div>
          <button class="mt-4 btn btn-outline" @click="clearFilter">
            Clear Filters
          </button>
        </div>

        <!-- Results -->
        <div v-else class="divide-y">
          <!-- Table -->
          <div v-for="customer in customers.data" :key="customer.id">
            <router-link :to="{ name: 'CustomerView', params: { id: customer.id } }" class="grid gap-4 px-5 py-4 text-gray-800 hover:no-underline hover:bg-gray-100">
              <div class="flex items-center">
                <Avatar 
                  :username="customer.name" 
                  :size="36" 
                  color="#fff" 
                  background-color="#718096" 
                  class="flex-none mr-4"
                  :custom-style="{ 'font-weight': 'bold' }" /> 
                <div class="grid items-center flex-grow grid-cols-2 leading-snug">
                  <div class="leading-snug">
                    <div class="font-medium">
                      {{ customer.name }}
                    </div>
                    <div class="text-gray-500">
                      {{ customer.email }}
                    </div>
                  </div>
                  <div class="leading-snug">
                    {{ customer.bookings_count }} {{ customer.bookings_count | pluralize('booking') }}
                  </div>
                </div>
              </div>
            </router-link>
          </div>
          <!-- Pagination -->
          <Pagination class="rounded-b-lg" :total="customers.meta.total" :per-page="customers.meta.per_page" :current-page="customers.meta.current_page" />
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import SortDropdown from '@/components/SortDropdown'
import Avatar from 'vue-avatar'

export default {
  name: 'CustomerList',

  metaInfo: {
    title: 'Customers'
  },

  components: {
    Avatar,
    SortDropdown
  },

  created () {
    this.setFilter()
    this.getCustomers()
  },

  computed: {
    ...mapGetters(['account'])
  },

  data () {
    return {
      submitting: false,
      loading: true,
      customers: {},
      filter: {
        search: ''
      },
      sortOptions: [
        { label: `Created <span class="text-gray-500">(Newest First)</span>`, prop: 'created_at', dir: 'desc' },
        { label: `Created <span class="text-gray-500">(Oldest First)</span>`, prop: 'created_at', dir: 'asc' },
        { label: `Last Updated <span class="text-gray-500">(Newest First)</span>`, prop: 'updated_at', dir: 'desc' },
        { label: `Last Updated <span class="text-gray-500">(Oldest First)</span>`, prop: 'updated_at', dir: 'asc' },
        { label: `Name <span class="text-gray-500">(A&rarr;Z)</span>`, prop: 'name', dir: 'asc' },
        { label: `Name <span class="text-gray-500">(Z&rarr;A)</span>`, prop: 'name', dir: 'desc' }
      ]
    }
  },

  methods: {
    async getCustomers () {
      try {
        this.loading = true
        const { data } = await this.$api.get(`account/${this.$route.params.account}/customers`, {
          params: {
            page: this.$route.query.page || 1,
            sort: this.$route.query.sort || 'created_at',
            dir: this.$route.query.dir || 'desc',
            ...this.filter && { filter: this.filter }
          }
        })
        this.customers = data.customers
        this.loading = false
      } catch (error) {
        console.error(error)
        this.$toasted.error(error.response.data.message)
      }
    },
    
    async syncCustomers () {
      try {
        this.submitting = true
        await this.$api.put(`account/${this.$route.params.account}/customers`)
        this.getCustomers()
      } catch (error) {
        console.error(error)
        this.$toasted.error(error.response.data.message)
      } finally {
        this.submitting = false
      }
    },

    setFilter () {
      if (this.$route.query.filter) {
        this.filter = JSON.parse(this.$route.query.filter)
      }
    },

    applyFilter () {
      this.$router.push({ 
        query: { 
          ...this.$route.query, 
          filter: JSON.stringify(this.filter)
        } 
      })
    },

    clearFilter () {
      this.filter = {
        search: '',
        orderdate: { start: sub(new Date(), { months: 1 }), end: add(new Date(), { months: 1 }) }
      }
      this.$router.push({ 
        query: { 
          ...this.$route.query, 
          filter: undefined
        } 
      })
    }
  },

  watch: {
    '$route.query': {
      handler (newVal, oldVal) {
        this.getCustomers()
      },
      deep: true
    }
  }
}
</script>